import { Component, EventEmitter, Input, OnDestroy, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { VariableDetails } from '../ontology/model/variable-details';
import { VariableService } from '../ontology/service/variable.service';
import { AlertService } from '../alert/alert.service';
import { finalize } from 'rxjs/operators';
import { VariableTypeEnum } from '../ontology/variable-type.enum';
import { VariableFilterRequest } from '../ontology/model/variable-filter-request';
import { DropdownOptionData } from '../column-filter/column-filter-dropdown-option-data';
import { SubscriptionsManager } from '../util/subscriptions-manager';

@Component({
    selector: 'jhi-variable-select',
    templateUrl: './variable-select.component.html'
})
export class VariableSelectComponent implements OnInit, OnDestroy, OnChanges {
    @Input() name: string;
    @Input() id: string;

    @Input() value: any;
    initialData: DropdownOptionData[];

    @Input() disabled = false;
    @Input() multiple = false;
    @Input() allowClear = false;

    // Filters
    @Input() variableTypeIds: VariableTypeEnum[];
    @Input() propertyIds: string[];
    @Input() datasetId: number;
    @Input() germplasmUUID: string;
    @Input() excludedVariableIds: number[];
    @Input() excludedVariableNames: string[];
    @Input() lotId: number;
    @Input() projectId: number;

    @Output() onVariableSelectedChange: EventEmitter<{ [key: string]: VariableDetails }> = new EventEmitter<{ [key: string]: VariableDetails }>();

    variables: any[];
    variableById: { [key: string]: VariableDetails } = {};
    isLoading = true;
    placeHolder: string;

    subscriptionManager = new SubscriptionsManager();

    constructor(
        private variableService: VariableService,
        private alertService: AlertService
    ) {}

    ngOnInit(): void {
        this.placeHolder = this.multiple ? 'select.variables' : 'select.variable';
        this.loadVariables();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.propertyIds ||
            changes.variableTypeIds ||
            changes.datasetId ||
            changes.germplasmUUID ||
            changes.projectId ||
            changes.lotId ||
            changes.disabled ||
            changes.excludedVariableIds ||
            changes.excludedVariableNames
        ) {
            this.loadVariables();
        }
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    private loadVariables(): void {
        const filterRequest = <VariableFilterRequest>{};

        if (this.datasetId) {
            filterRequest.datasetIds = [this.datasetId];
        }
        if (this.germplasmUUID) {
            filterRequest.germplasmUUIDs = [this.germplasmUUID];
        }
        if (this.variableTypeIds && this.variableTypeIds.length) {
            filterRequest.variableTypeIds = this.variableTypeIds.map((enumType) => enumType.toString());
        }
        if (this.propertyIds && this.propertyIds.length) {
            filterRequest.propertyIds = this.propertyIds;
        }
        if (this.projectId) {
            filterRequest.projectId = this.projectId;
        }
        if (this.lotId) {
            filterRequest.lotIds = [this.lotId];
        }
        if (this.disabled) {
            // show obsoletes when not editing
            filterRequest.showObsoletes = true;
        }

        this.isLoading = true;
        this.variableById = {};
        this.variables = [];
        this.initialData = [];

        this.subscriptionManager.add = this.variableService.filterVariables(filterRequest).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (variables) => {
                this.variables = this.transform(variables);

                if (this.value && this.variableById[this.value]) {
                    const variable = this.variableById[this.value];
                    this.initialData = [{ id: variable.id, text: variable.alias || variable.name }];
                } else {
                    this.value = this.multiple ? [] : null;
                    this.onVariableSelectedChange.emit(null);
                }
            },
            error: (error) => {
                this.alertService.error(error?.message || 'error.custom');
            }
        });
    }

    matcher(term: string, item: any): boolean {
        const termLowerCase = term.toLocaleLowerCase();
        return (item.alias && item.alias.toLocaleLowerCase().includes(termLowerCase))
            || item.name.toLocaleLowerCase().includes(termLowerCase);
    }

    transform(variables: VariableDetails[]): any[] {
        const filteredVariables = variables.filter((variable) => ((!this.excludedVariableIds || !this.excludedVariableIds.includes(parseInt(variable.id, 10)))
                && (!this.excludedVariableNames || !this.excludedVariableNames.includes(variable.name.toUpperCase()))));

        return filteredVariables.map((variable) => {
            this.variableById[variable.id] = variable;

            const copy: any = Object.assign({}, variable);
            const displayName = copy.alias
                ? copy.alias + ' (' + copy.name + ')'
                : copy.name;
            const classes = copy.property && copy.property.classes && copy.property.classes.length
                ? copy.property.classes.join(', ')
                : '';
            return Object.assign(copy, {
                id: copy.id,
                text: copy.alias || copy.name,
                displayName,
                propertyName: copy.property ? copy.property.name : '',
                classes,
                alias: copy.alias ? copy.alias + '(' + copy.name + ')' : ''
            });
        });
    }

    onNgModelChange() {
        if (!this.value) {
            this.onVariableSelectedChange.emit(null);
            return;
        }
        const event = this.multiple
            ? this.value.reduce((prev, id) => {
                prev[id] = this.variableById[id];
                return prev;
            }, {})
            : this.variableById[this.value];
        this.onVariableSelectedChange.emit(event);
    }

}
